import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useContext,
  useRef,
} from 'react';
import {SafeAreaView, View, ScrollView} from 'react-native';
import _ from 'lodash';
import {Portal, Divider} from 'react-native-paper';
import {ActivityIndicator} from 'react-native-web';

import sy from '~/styles';
import {
  Text,
  Pressable,
  Checkbox,
  PrimaryButton,
  LinearProgress,
  CloseButton,
} from '~/components/controls';
import {useDispatch, useSelector} from '~/lib/hooks';
import {addJobSetServices} from '~/actions';
import {Categories} from '~/types/service-codes';
import {dealer as dealerApi} from '~/api/public';
import PublicContentView from '~/components/shared/PublicContentView';
import ExpandMoreIcon from '~/images/md-icons/expand_more/materialicons/24px.svg';
import ExpandLessIcon from '~/images/md-icons/expand_less/materialicons/24px.svg';
import Format from '~/lib/format';
import PublicContentContext from '~/components/context/PublicContentContext';

import ShoppingCartIcon from '~/images/md-icons/shopping_cart/materialicons/24px.svg';

const ServiceRow = ({
  is_lease,
  service_code,
  title,
  price,
  related,
  checked,
  is_last,
  hasScrolledIntoView,
  onCheckedChange,
  onScrolledIntoView,
}) => {
  const rowRef = useRef();
  const {scrollRef} = useContext(PublicContentContext);

  const is_checked = checked.includes(service_code);
  const combo = related?.find((item) =>
    checked.includes(item.related_service_code),
  );

  const display_price = combo ? combo.price : price;
  const is_excluded = combo?.exclude;
  if (is_excluded && is_checked) {
    // Reset the state of this impossible combination.
    onCheckedChange(service_code);
  }

  const onLayout = async (e) => {
    if (
      !hasScrolledIntoView &&
      checked.length === 1 &&
      is_checked &&
      scrollRef
    ) {
      const TOP_BAR_HEIGHT = 64;
      const windowHeight = window?.innerHeight - TOP_BAR_HEIGHT;
      const {layout} = e.nativeEvent;

      if (layout.top + layout.height > windowHeight) {
        const scrollY = layout.top - windowHeight + layout.height;
        scrollRef.current.scrollTo({
          x: 0,
          y: scrollY,
        });
      }

      onScrolledIntoView(true);
    }
  };

  return (
    <>
      <View
        style={[sy['flex-row'], sy['items-center'], sy['px-4'], sy['gap-8']]}
        ref={rowRef}
        onLayout={onLayout}>
        <Checkbox
          disabled={is_excluded}
          checked={is_checked}
          onPress={() => onCheckedChange(service_code)}
        />
        <View
          style={[
            sy['flex-1'],
            sy['flex-row'],
            sy['items-center'],
            sy['justify-between'],
          ]}>
          <Text style={is_excluded && {color: '#828282'}}>{title}</Text>
          {!is_lease && (is_checked || combo) && !is_excluded && (
            <Text style={[sy.smallRegular, {color: '#828282'}]}>
              {_.isNumber(display_price)
                ? Format.price(display_price)
                : 'Prijs volgt'}
            </Text>
          )}
        </View>
      </View>
      {!is_last && (
        <Divider style={[sy['ml-18'], {backgroundColor: '#f2f2f2'}]} />
      )}
    </>
  );
};

const CollapsableCategory = ({
  title,
  titleStyle = sy.mediumPlus,
  children,
  defaultExpand = false,
}) => {
  const [expand, setExpand] = useState(defaultExpand);

  return (
    <>
      <View>
        <Pressable
          style={[
            sy['p-4'],
            sy['flex-row'],
            sy['items-center'],
            sy['justify-between'],
          ]}
          onPress={() => setExpand(!expand)}>
          <Text style={titleStyle}>{title}</Text>
          {expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Pressable>
        {expand && (
          <>
            <Divider style={{backgroundColor: '#f2f2f2'}} />
            {children}
          </>
        )}
      </View>
    </>
  );
};

const Type = ({navigation, route, onDismiss}) => {
  const dealer_id = route?.params?.dealer_id;
  const is_change = route?.params?.is_change;

  const dispatch = useDispatch();
  const dealer = useSelector((state) => state.dealer);
  const license = useSelector((state) => state.license);
  const mileage = useSelector((state) => state.mileage);
  const is_lease = useSelector((state) => state.is_lease);
  const storedServices = useSelector((state) => state.services);

  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState([]);
  const [services, setServices] = useState({});
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);

  useLayoutEffect(() => {
    navigation?.setOptions({
      headerShown: true,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () => (
        <LinearProgress style={{width: 126}} step={3} total={7} />
      ),
    });
  }, [navigation]);

  useEffect(() => {
    if (storedServices?.length) {
      setChecked(storedServices.map((item) => item.service_code));
    }
  }, [storedServices]);

  useEffect(() => {
    if (!license) {
      return;
    }

    const fetch = async () => {
      const services = await dealerApi.services(
        dealer.id,
        license,
        mileage,
        is_lease,
      );

      setServices(services);
      setLoading(false);
    };

    fetch();
  }, [license, dealer, mileage, is_lease, storedServices]);

  const onScrolledIntoView = () => {
    setHasScrolledIntoView(true);
  };

  const onCheckedChange = (service_code) => {
    if (checked.includes(service_code)) {
      checked.splice(checked.indexOf(service_code), 1);
    } else {
      checked.push(service_code);
    }

    setChecked(checked.slice(0));
  };

  const onNext = async () => {
    const all_services = Object.values(services).flatMap((items) => items);

    const selectedServices = all_services
      .filter((item) => checked.includes(item.service_code))
      .map(({id, service_code, title, price, related, sort_number}) => {
        const combo = related
          .sort((a, b) => a.order - b.order)
          .find((item) => checked.includes(item.related_service_code));

        return {
          id,
          service_code,
          title,
          price: combo ? combo.price : price,
          sort_number,
        };
      });

    dispatch(addJobSetServices(selectedServices));

    if (is_change && navigation.canGoBack()) {
      navigation.goBack();
    } else if (onDismiss) {
      onDismiss();
    } else {
      navigation.navigate('Date', {dealer_id});
    }
  };

  let {POPULAR: popular, ...rest} = services;
  let sticky = {
    title: Categories['POPULAR'],
    services: popular,
  };

  const content = (
    <>
      {loading && <ActivityIndicator size="large" color="#231fda" />}
      {!loading && (
        <>
          {!onDismiss ? (
            <>
              <Divider />
              <View style={[sy['p-4']]}>
                <Text style={sy.xxLarge}>Waar ben je naar op zoek?</Text>
              </View>
              <Divider />
            </>
          ) : (
            <View style={sy.modalHeaderView}>
              <CloseButton
                style={{position: 'absolute', top: 8, left: 8}}
                onPress={onDismiss}
              />
              <Text style={sy.medium}>Opdracht</Text>
            </View>
          )}
          <View style={[sy.contentView]}>
            {!loading && (
              <>
                <View>
                  <View
                    style={[
                      sy['p-4'],
                      sy['flex-row'],
                      sy['items-center'],
                      sy['justify-between'],
                    ]}>
                    <Text style={sy.mediumPlus}>{sticky.title}</Text>
                  </View>
                  <Divider style={{backgroundColor: '#f2f2f2'}} />
                  <View style={[sy['gap-4'], sy['py-4']]}>
                    {sticky.services
                      ?.map((service, index, array) => (
                        <ServiceRow
                          key={service.id}
                          is_lease={is_lease}
                          id={service.id}
                          service_code={service.service_code}
                          title={service.title}
                          price={service.price}
                          related={service.related}
                          is_last={index === array.length - 1}
                          checked={checked}
                          hasScrolledIntoView={hasScrolledIntoView}
                          onCheckedChange={onCheckedChange}
                          onScrolledIntoView={onScrolledIntoView}
                        />
                      ))
                      .filter(Boolean)}
                  </View>
                </View>
                <Divider style={{backgroundColor: '#f2f2f2'}} />
                {Object.keys(rest).map((category) => (
                  <React.Fragment key={category}>
                    <CollapsableCategory
                      title={Categories[category] || category}
                      titleStyle={sy.mediumPlus}
                      defaultExpand={rest[category]?.some((service) =>
                        checked.includes(service.service_code),
                      )}>
                      <View style={[sy['gap-4'], sy['py-4']]}>
                        {rest[category]?.map((service, index, array) => (
                          <ServiceRow
                            key={service.id}
                            is_lease={is_lease}
                            service_code={service.service_code}
                            title={service.title}
                            price={service.price}
                            related={service.related}
                            is_last={index === array.length - 1}
                            checked={checked}
                            hasScrolledIntoView={hasScrolledIntoView}
                            onCheckedChange={onCheckedChange}
                            onScrolledIntoView={onScrolledIntoView}
                          />
                        ))}
                      </View>
                    </CollapsableCategory>
                    <Divider style={{backgroundColor: '#f2f2f2'}} />
                  </React.Fragment>
                ))}
              </>
            )}
          </View>
        </>
      )}
    </>
  );

  const footer = (
    <View
      style={[
        sy.footerView,
        {
          borderTopWidth: 1,
          borderTopColor: '#dcdcdc',
        },
      ]}>
      <PrimaryButton
        disabled={loading}
        disableClickGuard={true}
        onPress={onNext}
        icon={
          is_change &&
          (() => <ShoppingCartIcon fill="#ffffff" width={18} height={18} />)
        }>
        {is_change ? 'Controleren' : onDismiss ? 'Opslaan' : 'Volgende'}
      </PrimaryButton>
    </View>
  );

  return (
    <Portal.Host>
      <SafeAreaView style={sy.mainView}>
        {onDismiss ? (
          <>
            <ScrollView>{content}</ScrollView>
            <View style={{borderTopWidth: 1, borderTopColor: '#dcdcdc'}}>
              {footer}
            </View>
          </>
        ) : (
          <PublicContentView>
            {{
              content,
              footer,
            }}
          </PublicContentView>
        )}
      </SafeAreaView>
    </Portal.Host>
  );
};

export default Type;
